import ImageThumbnails, { ImageWrapper } from "@components/ImageThumbnails"
import Stars from "@components/Stars"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body16BoldStyles, body16MainStyles } from "@constants/typography"
import { getIn } from "@utils/index"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import _ from "lodash-es"
import moment from "moment"
import React from "react"
import styled from "styled-components"

import { YotpoReview } from "../../../reviews/types"

const Container = styled.div``

const Rating = styled(Stars)`
  margin-bottom: 16px;
`

const Author = styled.div`
  ${body16MainStyles}
  margin-bottom: 16px;
  opacity: 0.7;
  color: ${colors.mediumDarkGray};
`

const Title = styled.div`
  ${body16BoldStyles}
  margin-bottom: 12px;
  color: ${colors.black};
`

const Body = styled.div`
  ${body16MainStyles}
  opacity: 0.5;
  color: ${colors.black};

  @media ${breakpoints.laptop} {
    ${body16MainStyles}
    margin-bottom: 24px;
    opacity: 1;
    color: ${colors.mediumGray};
  }
`

const Thumbnails = styled.div`
  ${ImageWrapper} {
    margin-right: 8px;
    margin-bottom: 24px;
  }
`

const formatName = (name: string) => {
  const startCased = _.startCase(name)
  const words = _.words(startCased)
  const lastWord = _.last(words)

  if (words.length > 1 && lastWord) {
    const lastWordIndex = _.lastIndexOf(words, lastWord)
    const firstLetter = lastWord.charAt(0)
    words[lastWordIndex] = `${firstLetter}.`
    return words.join(" ")
  }
  return name
}

interface ReviewProps {
  onClickImage: (id) => void
  review: YotpoReview
}

const Review: React.FC<ReviewProps> = ({ onClickImage, review }) => {
  const reviewer = getIn(review, "user.display_name")
  const createDate = getIn(review, "created_at")
  const title = getIn(review, "title")
  const body = getIn(review, "content")
  const rating = parseInt(getIn(review, "score"))

  const formattedReviewer = formatName(reviewer)
  const formattedCreateDate = moment(createDate).format("M/D/YY")

  const [parsedTitle, setParsedTitle] = React.useState(title)
  const [parsedBody, setParsedBody] = React.useState(body)

  React.useEffect(() => {
    if (DOMParser) {
      const decodeHtml = (str) => {
        const doc = new DOMParser().parseFromString(str, "text/html")
        return doc.documentElement.textContent
      }

      setParsedTitle(decodeHtml(title))
      setParsedBody(decodeHtml(body))
    }
  }, [title, body])

  let images

  if (review.images_data) {
    images = review.images_data.map((image: any) => ({
      alt: review.title,
      id: image.id,
      image: image.original_url,
      onClick: () => {
        onClickImage(image.id)
      },
      review: review.id,
      thumbnail: image.thumb_url,
    }))
  }

  return (
    <Container>
      <DesktopOnly>
        <Rating stars={rating} />
        <Author>{`${formattedReviewer} on ${formattedCreateDate}`}</Author>
        <Title>{parsedTitle}</Title>
        <Body>{parsedBody}</Body>
      </DesktopOnly>
      {images && (
        <Thumbnails>
          <ImageThumbnails images={images} imagesToShow={images.length} />
        </Thumbnails>
      )}
      <MobileAndTabletOnly>
        <Rating stars={rating} />
        <Author>{`${formattedReviewer} on ${formattedCreateDate}`}</Author>
        <Title>{parsedTitle}</Title>
        <Body>{parsedBody}</Body>
      </MobileAndTabletOnly>
    </Container>
  )
}

export default Review
